<script lang="ts" setup>
import { computed } from 'vue';
import type { AddressResponse } from '@/api';

const props = defineProps<{
  address: AddressResponse;
  addressLine1Title?: string;
  addressLine2Title?: string;
}>();

type AddressBlock = { title: string; lines: string[]; separated: boolean };

const formatLocality = (cityName: string, province: string) => (cityName.length > 0 && province.length > 0 ? `${cityName}, ${province}` : '');

const blocks = computed<AddressBlock[]>(() => {
  const { address } = props;
  const addressLine1Locality = [formatLocality(address.city.name, address.province), address.postalCode];
  const addressLine2Location = address.addressLine2Location;

  const candidates: AddressBlock[] = addressLine2Location
    ? [
        { title: props.addressLine1Title ?? '', lines: [address.addressLine1, ...addressLine1Locality], separated: true },
        { title: props.addressLine2Title ?? '', lines: [address.addressLine2, formatLocality(addressLine2Location.city.name, addressLine2Location.province), addressLine2Location.postalCode], separated: true },
      ]
    : [
        { title: props.addressLine1Title ?? '', lines: [address.addressLine1], separated: (props.addressLine1Title ?? '').length > 0 },
        { title: props.addressLine2Title ?? '', lines: [address.addressLine2], separated: (props.addressLine2Title ?? '').length > 0 },
        { title: '', lines: addressLine1Locality, separated: false },
      ];

  return candidates.map((block) => ({ ...block, lines: block.lines.filter((line) => line.length > 0) })).filter((block) => block.lines.length > 0);
});
</script>

<template>
  <div>
    <p v-for="(block, index) in blocks" :key="index" :class="{ 'mt-2': index > 0 && block.separated }">
      <strong v-if="block.title.length > 0" class="block font-semibold">{{ block.title }}</strong>
      <template v-for="(line, lineIndex) in block.lines" :key="lineIndex">{{ line }}<br v-if="lineIndex < block.lines.length - 1" /></template>
    </p>
  </div>
</template>
